import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { notesApiService } from '../services/notesApi';
import { Evidence } from '../types/player';
import { Identity } from '../types/auth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Eye, Trash2, File, X, Search, AlertCircle, FileText, Axe, TriangleAlert, ExternalLink, AlertTriangle, Calendar, Paperclip } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { CreatorProfile } from '../components/CreatorProfile';
import { ConfidentialEvidencePlaceholder } from '../components/ConfidentialEvidencePlaceholder';
import { ConfidentialBadge } from '../components/ConfidentialBadge';
import { ConfidentialToggle } from '../components/ConfidentialToggle';
import { isEvidenceRedacted, getEvidenceViewUrl } from '../utils/evidence';
import Fuse from 'fuse.js';
import { useIsMobile } from '../hooks/useIsMobile';
import { cn } from '../utils/cn';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

const getItemNavigationPath = (itemType: string, itemId: string): string | null => {
    switch (itemType) {
        case 'note':
        case 'warning':
            return `/notes-warnings?highlight=${encodeURIComponent(itemId)}`;
        case 'ban':
            return `/bans?highlight=${encodeURIComponent(itemId)}`;
        case 'incident':
            return `/incidents/${encodeURIComponent(itemId)}`;
        case 'calendar_event':
            return `/calendar`;
        default:
            return null;
    }
};

const getItemTypeIcon = (itemType: string) => {
    switch (itemType) {
        case 'note': return <FileText className="w-3 h-3 text-blue-400" />;
        case 'warning': return <TriangleAlert className="w-3 h-3 text-yellow-400" />;
        case 'ban': return <Axe className="w-3 h-3 text-red-400" />;
        case 'incident': return <AlertTriangle className="w-3 h-3 text-purple-400" />;
        case 'calendar_event': return <Calendar className="w-3 h-3 text-green-400" />;
        default: return null;
    }
};

interface EvidenceCardProps {
    item: Evidence;
    onDelete: (id: string) => void;
    onToggleConfidential: (id: string, newValue: boolean) => Promise<void>;
    canDelete: boolean;
    canEdit: boolean;
    identity: Identity | null;
}

const EvidenceCard = ({ item, onDelete, onToggleConfidential, canDelete, canEdit, identity }: EvidenceCardProps) => {
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const isMobile = useIsMobile();
    const navigate = useNavigate();

    const redacted = isEvidenceRedacted(item, identity);

    const handleView = () => {
        if (redacted) return;
        if (item.content_type.startsWith('image/')) {
            setIsLightboxOpen(true);
        } else {
            window.open(getEvidenceViewUrl(item.id), '_blank');
        }
    };

    const handleNavigateToItem = () => {
        if (!item.item_id || !item.item_type) return;
        const path = getItemNavigationPath(item.item_type, item.item_id);
        if (path) {
            navigate(path);
        }
    };

    return (
        <>
            <div className="glass-card p-3 group">
                <div className="relative aspect-square bg-dark-800 rounded-md overflow-hidden mb-2">
                    {redacted ? (
                        <ConfidentialEvidencePlaceholder />
                    ) : item.content_type.startsWith('image/') ? (
                        <img 
                            src={getEvidenceViewUrl(item.id)} 
                            alt={item.filename}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                (e.target as HTMLImageElement).parentElement?.classList.add('evidence-img-error');
                            }}
                        />
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-glass-400 p-2">
                            <File className="w-10 h-10" />
                        </div>
                    )}
                    {!redacted && (
                        <div className={cn("absolute inset-0 bg-black/70 transition-opacity flex items-center justify-center gap-2", isMobile ? "opacity-100 bg-black/40" : "opacity-0 group-hover:opacity-100")}>
                            <button onClick={handleView} className="p-2 rounded-full bg-glass-200/20 hover:bg-blue-500/50 text-white" title="View" aria-label="View evidence"><Eye className="w-5 h-5" /></button>
                            {canEdit && (
                                <ConfidentialToggle
                                    currentValue={item.confidential}
                                    onToggle={(newValue) => onToggleConfidential(item.id, newValue)}
                                />
                            )}
                            {canDelete && (
                                <button onClick={() => onDelete(item.id)} className="p-2 rounded-full bg-glass-200/20 hover:bg-red-500/50 text-white" title="Delete" aria-label="Delete evidence"><Trash2 className="w-5 h-5" /></button>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-white truncate flex-1" title={item.filename}>{item.filename}</p>
                    {item.confidential && !redacted && <ConfidentialBadge />}
                </div>
                <div className="text-xs text-glass-400">
                    Uploaded by <CreatorProfile creatorId={item.uploader_id} inline />
                </div>
                <div className="text-xs text-glass-500 tabular-nums">
                    {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                </div>
                {item.item_id && (
                    <button
                        onClick={handleNavigateToItem}
                        className="w-full text-left text-xs text-glass-300 mt-2 flex items-center gap-1 border-t border-glass-200/10 pt-2 hover:text-accent-300 transition-colors cursor-pointer group/link"
                        title={`View associated ${item.item_type}`}
                    >
                        {getItemTypeIcon(item.item_type)}
                        <span className="capitalize">{item.item_type}:</span>
                        <span className="font-mono">{item.item_id.substring(0, 8)}...</span>
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover/link:opacity-100 transition-opacity ml-auto" />
                    </button>
                )}
            </div>
            {isLightboxOpen && !redacted && (
                 <div className="modal-overlay z-50" onClick={() => setIsLightboxOpen(false)}>
                    <img 
                        src={getEvidenceViewUrl(item.id)} 
                        alt={item.filename}
                        className="max-w-[90vw] max-h-[90vh] object-contain"
                    />
                    <button className="absolute top-4 right-4 text-white hover:text-gray-300" title="Close" aria-label="Close">
                        <X className="w-8 h-8" />
                    </button>
                </div>
            )}
        </>
    );
};


export const EvidenceView = () => {
    useDocumentTitle('Evidence');
    const [allEvidence, setAllEvidence] = useState<Evidence[]>([]);
    const [filteredEvidence, setFilteredEvidence] = useState<Evidence[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const { identity } = useAuth();
    
    const canDelete = useMemo(() => {
        if (!identity) return false;
        const groups = new Set(identity.raw.custom.groups);
        return groups.has('SPY_Notes_Editor') || groups.has('SPY_Super_admin');
    }, [identity]);

    const canEdit = useMemo(() => {
        if (!identity) return false;
        const groups = new Set(identity.raw.custom.groups);
        return groups.has('SPY_Notes') || groups.has('SPY_Notes_Editor') || groups.has('SPY_Super_admin');
    }, [identity]);

    useEffect(() => {
        const fetchAllEvidence = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await notesApiService.getAllEvidence();
                setAllEvidence(data);
                setFilteredEvidence(data);
            } catch (err) {
                setError('Failed to load evidence.');
            } finally {
                setLoading(false);
            }
        };
        fetchAllEvidence();
    }, []);

    const fuse = useMemo(() => {
        return new Fuse(allEvidence, {
            keys: ['filename', 'uploader_id', 'item_id', 'item_type'],
            includeScore: true,
            threshold: 0.4,
        });
    }, [allEvidence]);

    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredEvidence(allEvidence);
        } else {
            const results = fuse.search(searchQuery);
            setFilteredEvidence(results.map(result => result.item));
        }
    }, [searchQuery, allEvidence, fuse]);

    const handleDelete = async (evidenceId: string) => {
        if (window.confirm('Are you sure you want to permanently delete this evidence?')) {
            try {
                await notesApiService.deleteEvidence(evidenceId);
                setAllEvidence(prev => prev.filter(e => e.id !== evidenceId));
            } catch (err) {
                alert('Failed to delete evidence.');
            }
        }
    };

    const handleToggleConfidential = async (evidenceId: string, newValue: boolean) => {
        try {
            const updated = await notesApiService.patchEvidence(evidenceId, { confidential: newValue });
            setAllEvidence(prev => prev.map(e => e.id === evidenceId ? updated : e));
        } catch {
            alert('Failed to update confidentiality.');
        }
    };
    
    const hasQuery = searchQuery.trim() !== '';

    return (
        <div className="space-y-6">
            <PageHeader
                eyebrow="Moderation"
                title="Evidence Explorer"
                description="View all uploaded evidence across the network."
                icon={Paperclip}
                meta={!loading && !error ? (
                    <span className="badge badge-neutral">{allEvidence.length.toLocaleString()} file{allEvidence.length === 1 ? '' : 's'}</span>
                ) : undefined}
            >
                <div className="flex flex-col md:flex-row md:items-center gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                        <input 
                            type="text" 
                            placeholder="Search by filename, uploader, item ID or type…" 
                            className="glass-input w-full pl-9"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                    {!loading && !error && (
                        <span className="text-xs text-glass-400 tabular-nums md:text-right">
                            {hasQuery
                                ? `${filteredEvidence.length.toLocaleString()} match${filteredEvidence.length === 1 ? '' : 'es'}`
                                : `${filteredEvidence.length.toLocaleString()} file${filteredEvidence.length === 1 ? '' : 's'}`}
                        </span>
                    )}
                </div>
            </PageHeader>

            {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" aria-busy="true">
                    {Array.from({ length: 10 }).map((_, i) => (
                        <div key={i} className="glass-card p-3 space-y-2">
                            <SkeletonLoader className="aspect-square w-full rounded-md" />
                            <SkeletonLoader className="h-4 w-3/4" />
                            <SkeletonLoader className="h-3 w-1/2" />
                        </div>
                    ))}
                </div>
            )}
            {error && (
                <EmptyState
                    tone="danger"
                    icon={AlertCircle}
                    title="Failed to load evidence"
                    description={error}
                />
            )}

            {!loading && !error && filteredEvidence.length > 0 && (
                 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {filteredEvidence.map(item => (
                        <EvidenceCard
                            key={item.id}
                            item={item}
                            onDelete={handleDelete}
                            onToggleConfidential={handleToggleConfidential}
                            canDelete={canDelete}
                            canEdit={canEdit}
                            identity={identity}
                        />
                    ))}
                </div>
            )}

            {!loading && !error && filteredEvidence.length === 0 && (
                <EmptyState
                    icon={hasQuery ? Search : Paperclip}
                    title={hasQuery ? 'No evidence matches your search' : 'No evidence found'}
                    description={hasQuery ? 'Try adjusting your search query.' : 'Uploaded evidence from notes, warnings, bans and incidents will appear here.'}
                    action={hasQuery ? <button onClick={() => setSearchQuery('')} className="glass-button-secondary">Clear search</button> : undefined}
                />
            )}
        </div>
    );
}
